import {
  DocumentData,
  QueryConstraint,
  collectionGroup,
  count,
  doc,
  getAggregateFromServer,
  getDoc,
  getDocs,
  limit,
  query,
  where
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { COLLECTIONS } from "./collectionNames";
import { adminCollection, dateField, logDevelopmentError, stringArrayField, stringField } from "./firestoreHelpers";

export type FounderCommunityStatus = "healthy" | "growing" | "watching" | "future" | "unavailable";

export type FounderCommunityMetricId =
  | "activeDancers"
  | "activeCoaches"
  | "activeStudios"
  | "messagesSent"
  | "bookingsCompleted"
  | "challengesSubmitted"
  | "comments"
  | "loves"
  | "follows"
  | "guardianConnections"
  | "communityHealthScore";

export type FounderCommunityMetric = {
  id: FounderCommunityMetricId;
  title: string;
  value: string;
  story: string;
  detail: string;
  source: string;
  status: FounderCommunityStatus;
  unavailableReason: string | null;
};

export type FounderCommunityHighlightId =
  | "strongestStudio"
  | "mostActiveCoach"
  | "fastestGrowingStudio"
  | "mostBookedService"
  | "topChallenge";

export type FounderCommunityHighlight = {
  id: FounderCommunityHighlightId;
  title: string;
  value: string;
  story: string;
  source: string;
  status: FounderCommunityStatus;
};

export type FounderCommunityStory = {
  id: string;
  icon: string;
  headline: string;
  insight: string;
  status: FounderCommunityStatus;
};

export type FounderCommunityDashboard = {
  hero: FounderCommunityStory;
  stories: FounderCommunityStory[];
  metrics: FounderCommunityMetric[];
  highlights: FounderCommunityHighlight[];
  lastUpdated: Date;
  error: string | null;
};

type CountResult = {
  value: number | null;
  unavailableReason: string | null;
};

const RECENT_SAMPLE_LIMIT = 500;
const ACTIVE_WINDOW_DAYS = 30;

function startOfLocalDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function formatCount(value: number | null) {
  return value === null ? "Unavailable" : new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
}

function metricStatus(value: number | null): FounderCommunityStatus {
  if (value === null) return "unavailable";
  return value > 0 ? "healthy" : "watching";
}

function metric(
  id: FounderCommunityMetricId,
  title: string,
  value: string,
  story: string,
  detail: string,
  source: string,
  status: FounderCommunityStatus,
  unavailableReason: string | null = null
): FounderCommunityMetric {
  return {
    id,
    title,
    value,
    story,
    detail,
    source,
    status,
    unavailableReason
  };
}

function unavailableMetric(id: FounderCommunityMetricId, title: string, source: string, story: string, reason: string) {
  return metric(id, title, "Unavailable", story, reason, source, "unavailable", reason);
}

async function safeCount(collectionName: string, constraints: QueryConstraint[] = []): Promise<CountResult> {
  try {
    const snapshot = await getAggregateFromServer(query(adminCollection(collectionName), ...constraints), {
      total: count()
    });

    return { value: snapshot.data().total ?? 0, unavailableReason: null };
  } catch (error) {
    logDevelopmentError(error);
    return { value: null, unavailableReason: "The aggregate query could not be read." };
  }
}

async function safeCollectionGroupCount(groupName: string, constraints: QueryConstraint[] = []): Promise<CountResult> {
  try {
    const snapshot = await getAggregateFromServer(query(collectionGroup(db, groupName), ...constraints), {
      total: count()
    });

    return { value: snapshot.data().total ?? 0, unavailableReason: null };
  } catch (error) {
    logDevelopmentError(error);
    return { value: null, unavailableReason: `The ${groupName} collection-group aggregate could not be read.` };
  }
}

function userRoles(data: DocumentData) {
  const roles = new Set<string>();
  const stringRoleFields = ["role", "activeRole", "primaryRole", "accountType", "verificationType"];
  const arrayRoleFields = ["roles", "assignedRoles", "completedRoleTargets"];

  stringRoleFields.forEach((field) => {
    const value = stringField(data, field);
    if (value) {
      roles.add(value.toLowerCase());
    }
  });

  arrayRoleFields.forEach((field) => {
    stringArrayField(data, field).forEach((value) => roles.add(value.toLowerCase()));
  });

  if (data.isDancer === true) roles.add("dancer");
  if (data.isCoach === true) roles.add("coach");
  if (data.isStudio === true || data.isStudioOwner === true) roles.add("studio");

  return roles;
}

function hasAnyRole(roles: Set<string>, acceptedRoles: string[]) {
  return acceptedRoles.some((role) => roles.has(role.toLowerCase()));
}

function nonNegativeNumber(data: DocumentData, key: string) {
  const value = data[key];
  return typeof value === "number" && Number.isFinite(value) ? Math.max(value, 0) : 0;
}

function displayName(data: DocumentData, fallback: string) {
  return stringField(data, "displayName") ?? stringField(data, "studioName") ?? stringField(data, "fullName") ?? stringField(data, "username") ?? fallback;
}

async function userDisplayName(userId: string) {
  try {
    const snapshot = await getDoc(doc(db, COLLECTIONS.users, userId));
    return snapshot.exists() ? displayName(snapshot.data(), "Unknown member") : "Unknown member";
  } catch (error) {
    logDevelopmentError(error);
    return "Unknown member";
  }
}

async function activeRoleCounts(activeSince: Date) {
  try {
    const snapshot = await getDocs(
      query(adminCollection(COLLECTIONS.users), where("lastSeenAt", ">=", activeSince), limit(RECENT_SAMPLE_LIMIT))
    );

    let dancers = 0;
    let coaches = 0;
    let studios = 0;

    snapshot.docs.forEach((document) => {
      const roles = userRoles(document.data());
      if (hasAnyRole(roles, ["dancer", "minor_dancer", "adult_dancer"])) dancers += 1;
      if (hasAnyRole(roles, ["coach"])) coaches += 1;
      if (hasAnyRole(roles, ["studio", "studioowner", "studio_owner"])) studios += 1;
    });

    return { dancers, coaches, studios, unavailableReason: null };
  } catch (error) {
    logDevelopmentError(error);
    return { dancers: null, coaches: null, studios: null, unavailableReason: "Active user role records could not be read." };
  }
}

async function sampleUsersByRole(roleAliases: string[]) {
  try {
    const snapshot = await getDocs(query(adminCollection(COLLECTIONS.users), limit(RECENT_SAMPLE_LIMIT)));
    return snapshot.docs
      .filter((document) => hasAnyRole(userRoles(document.data()), roleAliases))
      .map((document) => ({ id: document.id, data: document.data() }));
  } catch (error) {
    logDevelopmentError(error);
    return [];
  }
}

async function strongestStudio(): Promise<FounderCommunityHighlight> {
  const studios = await sampleUsersByRole(["studio", "studioowner", "studio_owner"]);
  const strongest = studios.sort((lhs, rhs) => nonNegativeNumber(rhs.data, "followerCount") - nonNegativeNumber(lhs.data, "followerCount"))[0];

  if (!strongest) {
    return {
      id: "strongestStudio",
      title: "Strongest Studio",
      value: "Unavailable",
      story: "Studio relationship strength will appear when studio role records are available.",
      source: COLLECTIONS.users,
      status: "unavailable"
    };
  }

  const followers = nonNegativeNumber(strongest.data, "followerCount");
  return {
    id: "strongestStudio",
    title: "Strongest Studio",
    value: displayName(strongest.data, "Studio"),
    story: `${displayName(strongest.data, "This studio")} has ${formatCount(followers)} followers.`,
    source: "users.followerCount",
    status: followers > 0 ? "healthy" : "watching"
  };
}

async function fastestGrowingStudio(weekStart: Date, previousWeekStart: Date): Promise<FounderCommunityHighlight> {
  const studios = await sampleUsersByRole(["studio", "studioowner", "studio_owner"]);
  const studioIds = new Map(studios.map((studio) => [studio.id, studio.data]));

  if (studioIds.size === 0) {
    return {
      id: "fastestGrowingStudio",
      title: "Fastest Growing Studio",
      value: "Unavailable",
      story: "Studio follow growth will appear when studio records are available.",
      source: COLLECTIONS.users,
      status: "unavailable"
    };
  }

  try {
    const snapshot = await getDocs(
      query(adminCollection(COLLECTIONS.follows), where("createdAt", ">=", previousWeekStart), limit(RECENT_SAMPLE_LIMIT))
    );
    const current = new Map<string, number>();
    const previous = new Map<string, number>();

    snapshot.docs.forEach((document) => {
      const data = document.data();
      const targetId = stringField(data, "followingUserId") ?? stringField(data, "followingID") ?? stringField(data, "targetUserId");
      const createdAt = dateField(data, "createdAt");
      if (!targetId || !createdAt || !studioIds.has(targetId)) return;

      const bucket = createdAt >= weekStart ? current : previous;
      bucket.set(targetId, (bucket.get(targetId) ?? 0) + 1);
    });

    const [studioId, growth] =
      Array.from(current.entries()).sort((lhs, rhs) => rhs[1] - (previous.get(rhs[0]) ?? 0) - (lhs[1] - (previous.get(lhs[0]) ?? 0)))[0] ?? [];

    if (!studioId) {
      return {
        id: "fastestGrowingStudio",
        title: "Fastest Growing Studio",
        value: "No clear leader yet",
        story: "No studio gained new follows in the current week sample.",
        source: COLLECTIONS.follows,
        status: "watching"
      };
    }

    const name = displayName(studioIds.get(studioId) ?? {}, "This studio");
    return {
      id: "fastestGrowingStudio",
      title: "Fastest Growing Studio",
      value: name,
      story: `${name} added ${formatCount(growth)} new follows this week.`,
      source: "follows.createdAt",
      status: "growing"
    };
  } catch (error) {
    logDevelopmentError(error);
    return {
      id: "fastestGrowingStudio",
      title: "Fastest Growing Studio",
      value: "Unavailable",
      story: "Studio follow growth could not be read.",
      source: COLLECTIONS.follows,
      status: "unavailable"
    };
  }
}

async function topBookingHighlight(
  id: "mostActiveCoach" | "mostBookedService",
  title: string,
  field: string,
  fallback: string,
  storyBuilder: (value: string, count: number) => string
): Promise<FounderCommunityHighlight> {
  try {
    const snapshot = await getDocs(query(adminCollection(COLLECTIONS.bookings), where("createdAt", ">=", addDays(new Date(), -30)), limit(RECENT_SAMPLE_LIMIT)));
    const counts = new Map<string, number>();

    snapshot.docs.forEach((document) => {
      const data = document.data();
      const value = stringField(data, field);
      if (!value) return;
      counts.set(value, (counts.get(value) ?? 0) + 1);
    });

    const [key, total] = Array.from(counts.entries()).sort((lhs, rhs) => rhs[1] - lhs[1])[0] ?? [];

    if (!key) {
      return {
        id,
        title,
        value: "No leader yet",
        story: fallback,
        source: COLLECTIONS.bookings,
        status: "watching"
      };
    }

    const label = field === "providerUserID" ? await userDisplayName(key) : key;
    return {
      id,
      title,
      value: label,
      story: storyBuilder(label, total),
      source: `bookings.${field}`,
      status: "healthy"
    };
  } catch (error) {
    logDevelopmentError(error);
    return {
      id,
      title,
      value: "Unavailable",
      story: `${title} could not be read from booking records.`,
      source: COLLECTIONS.bookings,
      status: "unavailable"
    };
  }
}

async function topChallenge(): Promise<FounderCommunityHighlight> {
  try {
    const snapshot = await getDocs(query(adminCollection(COLLECTIONS.challengeSubmissions), limit(RECENT_SAMPLE_LIMIT)));
    const counts = new Map<string, number>();

    snapshot.docs.forEach((document) => {
      const challengeId = stringField(document.data(), "challengeId");
      if (!challengeId) return;
      counts.set(challengeId, (counts.get(challengeId) ?? 0) + 1);
    });

    const [challengeId, total] = Array.from(counts.entries()).sort((lhs, rhs) => rhs[1] - lhs[1])[0] ?? [];

    if (!challengeId) {
      return {
        id: "topChallenge",
        title: "Top Challenge",
        value: "No leader yet",
        story: "Challenge submissions will identify the top challenge as the community participates.",
        source: COLLECTIONS.challengeSubmissions,
        status: "watching"
      };
    }

    const challengeSnapshot = await getDoc(doc(db, COLLECTIONS.challenges, challengeId));
    const title = challengeSnapshot.exists() ? stringField(challengeSnapshot.data(), "title") ?? "This challenge" : "This challenge";
    return {
      id: "topChallenge",
      title: "Top Challenge",
      value: title,
      story: `${title} has ${formatCount(total)} submissions in the sampled challenge activity.`,
      source: "submissions.challengeId",
      status: "healthy"
    };
  } catch (error) {
    logDevelopmentError(error);
    return {
      id: "topChallenge",
      title: "Top Challenge",
      value: "Unavailable",
      story: "Top challenge could not be read from submission records.",
      source: COLLECTIONS.challengeSubmissions,
      status: "unavailable"
    };
  }
}

function communityHero(activeDancers: number | null, messagesSent: number | null, follows: number | null): FounderCommunityStory {
  if (activeDancers !== null && activeDancers > 0) {
    return {
      id: "active-dancers",
      icon: "❤️",
      headline: `${formatCount(activeDancers)} active dancers.`,
      insight: "Dancer activity is the heartbeat of the Chor community.",
      status: "healthy"
    };
  }

  if (messagesSent !== null && messagesSent > 0) {
    return {
      id: "messages",
      icon: "💬",
      headline: `${formatCount(messagesSent)} messages sent today.`,
      insight: "Conversation shows the community is moving together.",
      status: "healthy"
    };
  }

  return {
    id: "follows",
    icon: "🤝",
    headline: `${formatCount(follows)} follows today.`,
    insight: "Relationship growth will make the community feel connected.",
    status: metricStatus(follows)
  };
}

export async function fetchFounderCommunityDashboard(now = new Date()): Promise<FounderCommunityDashboard> {
  const todayStart = startOfLocalDay(now);
  const tomorrowStart = addDays(todayStart, 1);
  const activeSince = addDays(todayStart, -ACTIVE_WINDOW_DAYS);
  const weekStart = addDays(todayStart, -7);
  const previousWeekStart = addDays(todayStart, -14);
  const createdToday = [where("createdAt", ">=", todayStart), where("createdAt", "<", tomorrowStart)];

  const [
    activeRoles,
    messagesSent,
    bookingsCompleted,
    challengesSubmitted,
    comments,
    postLikes,
    challengeLoves,
    follows,
    guardianConnections,
    strongest,
    growingStudio,
    activeCoach,
    bookedService,
    challenge
  ] = await Promise.all([
    activeRoleCounts(activeSince),
    safeCollectionGroupCount("messages", createdToday),
    safeCount(COLLECTIONS.bookings, [where("status", "==", "completed"), where("createdAt", ">=", todayStart), where("createdAt", "<", tomorrowStart)]),
    safeCount(COLLECTIONS.challengeSubmissions, createdToday),
    safeCollectionGroupCount("comments", createdToday),
    safeCount(COLLECTIONS.postLikes, createdToday),
    safeCount(COLLECTIONS.challengeSubmissionLoves, createdToday),
    safeCount(COLLECTIONS.follows, createdToday),
    safeCount(COLLECTIONS.guardianLinks, [where("status", "==", "active")]),
    strongestStudio(),
    fastestGrowingStudio(weekStart, previousWeekStart),
    topBookingHighlight("mostActiveCoach", "Most Active Coach", "providerUserID", "Coach activity will appear once bookings are recorded.", (coach, total) => `${coach} has ${formatCount(total)} recent bookings.`),
    topBookingHighlight("mostBookedService", "Most Booked Service", "serviceTitle", "Service demand will appear once dancers book services.", (service, total) => `${service} is the most booked service in recent activity.`),
    topChallenge()
  ]);

  const lovesUnavailableReason = postLikes.unavailableReason ?? challengeLoves.unavailableReason;
  const totalLoves = postLikes.value === null && challengeLoves.value === null ? null : (postLikes.value ?? 0) + (challengeLoves.value ?? 0);

  const metrics: FounderCommunityMetric[] = [
    metric("activeDancers", "Active Dancers", formatCount(activeRoles.dancers), `${formatCount(activeRoles.dancers)} dancers were active in the last ${ACTIVE_WINDOW_DAYS} days.`, "Active dancers derive from users with dancer role signals and recent lastSeenAt.", "users.lastSeenAt + role fields", metricStatus(activeRoles.dancers), activeRoles.unavailableReason),
    metric("activeCoaches", "Active Coaches", formatCount(activeRoles.coaches), `${formatCount(activeRoles.coaches)} coaches were active in the last ${ACTIVE_WINDOW_DAYS} days.`, "Active coaches derive from users with coach role signals and recent lastSeenAt.", "users.lastSeenAt + role fields", metricStatus(activeRoles.coaches), activeRoles.unavailableReason),
    metric("activeStudios", "Active Studios", formatCount(activeRoles.studios), `${formatCount(activeRoles.studios)} studios were active in the last ${ACTIVE_WINDOW_DAYS} days.`, "Active studios derive from users with studio role signals and recent lastSeenAt.", "users.lastSeenAt + role fields", metricStatus(activeRoles.studios), activeRoles.unavailableReason),
    metric("messagesSent", "Messages Sent", formatCount(messagesSent.value), `${formatCount(messagesSent.value)} messages were sent today.`, "Messages are counted from conversation message subcollections.", "collectionGroup(messages).createdAt", metricStatus(messagesSent.value), messagesSent.unavailableReason),
    metric("bookingsCompleted", "Bookings Completed", formatCount(bookingsCompleted.value), `${formatCount(bookingsCompleted.value)} bookings were completed today.`, "Completed booking count reads canonical booking records.", "bookings.status + createdAt", metricStatus(bookingsCompleted.value), bookingsCompleted.unavailableReason),
    metric("challengesSubmitted", "Challenges Submitted", formatCount(challengesSubmitted.value), `${formatCount(challengesSubmitted.value)} challenge submissions arrived today.`, "Challenge submissions derive from canonical submission records.", "submissions.createdAt", metricStatus(challengesSubmitted.value), challengesSubmitted.unavailableReason),
    metric("comments", "Comments", formatCount(comments.value), `${formatCount(comments.value)} comments were added today.`, "Comments are counted from post comment subcollections.", "collectionGroup(comments).createdAt", metricStatus(comments.value), comments.unavailableReason),
    metric("loves", "Loves", formatCount(totalLoves), `${formatCount(totalLoves)} loves were given today.`, "Loves combine canonical postLikes and challengeSubmissionLoves.", "postLikes + challengeSubmissionLoves", metricStatus(totalLoves), lovesUnavailableReason),
    metric("follows", "Follows", formatCount(follows.value), `${formatCount(follows.value)} new follows connected the community today.`, "Follow relationships derive from the canonical follows graph.", "follows.createdAt", metricStatus(follows.value), follows.unavailableReason),
    metric("guardianConnections", "Guardian Connections", formatCount(guardianConnections.value), `${formatCount(guardianConnections.value)} guardian connections are active.`, "Guardian connections derive from active guardianLinks.", "guardianLinks.status", metricStatus(guardianConnections.value), guardianConnections.unavailableReason),
    unavailableMetric("communityHealthScore", "Community Health Score", "Future aggregate", "Community Health Score is a future rollup.", "No canonical community health score aggregate exists yet.")
  ];

  const stories: FounderCommunityStory[] = [
    {
      id: "relationships",
      icon: "🤝",
      headline: `${formatCount(follows.value)} follows today.`,
      insight: follows.value && follows.value > 0 ? "People are choosing to stay connected." : "Relationship activity is quiet today.",
      status: metricStatus(follows.value)
    },
    {
      id: "encouragement",
      icon: "❤️",
      headline: `${formatCount(totalLoves)} loves today.`,
      insight: totalLoves && totalLoves > 0 ? "The community is showing appreciation." : "Appreciation signals will appear as dancers engage.",
      status: metricStatus(totalLoves)
    },
    {
      id: "challenge-energy",
      icon: "🏆",
      headline: `${formatCount(challengesSubmitted.value)} challenge submissions today.`,
      insight: challengesSubmitted.value && challengesSubmitted.value > 0 ? "Challenges are turning into participation." : "Challenge participation has not started today.",
      status: metricStatus(challengesSubmitted.value)
    }
  ];

  return {
    hero: communityHero(activeRoles.dancers, messagesSent.value, follows.value),
    stories,
    metrics,
    highlights: [strongest, activeCoach, growingStudio, bookedService, challenge],
    lastUpdated: new Date(),
    error: null
  };
}
